<template>
    <section class="sol">
        <div class="container">
            <h2 class="sol-title">应用行业</h2>
            <p class="sol-subtitle">
                技明的制造加工能力与自研工业定位灯，广泛应用于工业产线、汽车制造、仓储物流、机械加工、电子电器与照明工程等领域，为多元化生产场景提供可靠支持。
            </p>

            <div class="panel">
                <!-- 左侧菜单 -->
                <div class="left">
                    <ul class="menu">
                        <li v-for="t in tabs" :key="t.key" class="menu-item">
                            <button class="menu-btn" :class="{ active: activeKey === t.key }" type="button"
                                @mouseenter="setActive(t.key)" @focus="setActive(t.key)">
                                {{ t.label }}
                            </button>
                        </li>
                    </ul>
                </div>

                <!-- 中间竖线 -->
                <div class="divider" />

                <!-- 右侧内容 -->
                <div class="right">
                    <template v-if="activeTab">
                        <h3 class="right-title">{{ activeTab.panel.title }}</h3>

                        <ul class="bullets">
                            <li v-for="(b, i) in activeTab.panel.bullets" :key="i">{{ b }}</li>
                        </ul>

                        <div class="clients">
                            <div class="client" v-for="(c, i) in activeTab.panel.clients" :key="i">{{ c }}</div>
                        </div>

                        <div class="actions">
                            <a class="btn btn-ghost" href="tel:13958349372">立即咨询 ></a>
                            <button class="btn btn-outline" type="button" @click="$router.push('/productService')">了解更多 ></button>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, ref } from "vue";

const tabs = [
    {
        key: "line",
        label: "工业产线",
        panel: {
            title: "工业产线装配",
            bullets: [
                "为精密设备装配、电子元件焊接等工位提供毫米级定位，提升作业准确率。",
                "支持多工位统一标准定位，减少人工对位误差，提升产线节拍与效率。",
                "结合生产线实际情况提供定制化方案，灵活适配不同工艺要求。",
            ],
            clients: ["装配制造", "电子电器", "零部件加工", "设备集成", "产线改造"],
        },
    },
    {
        key: "auto",
        label: "汽车制造",
        panel: {
            title: "汽车零部件制造",
            bullets: [
                "适配汽车零部件、内外饰件等精密加工场景的定位与工序指引。",
                "耐环境、抗干扰的设计，适应车间高温、粉尘等复杂工况。",
                "稳定的结构与耐用材质，满足批量、连续生产的长期使用需求。",
            ],
            clients: ["汽车部件", "内外饰件", "模具加工", "冲压焊接", "整车配套"],
        },
    },
    {
        key: "logistics",
        label: "仓储物流",
        panel: {
            title: "仓储与物流分拣",
            bullets: [
                "货架货位、分拣口提供清晰的视觉指引，降低错拣、漏拣概率。",
                "支持与 AGV、输送线等设备协同，优化场内搬运与分拣流程。",
                "强光环境下依然清晰可见，助力昼夜连续作业。",
            ],
            clients: ["电商仓配", "制造仓储", "物流分拣", "智能立体库", "冷链仓库"],
        },
    },
    {
        key: "machining",
        label: "机械加工",
        panel: {
            title: "机械加工与设备运维",
            bullets: [
                "为大型机械检修点位、管道铺设等场景提供精准标记与定位。",
                "抗强震、耐油污的特性，适应机床、产线等高频振动环境。",
                "支持按需定制投射图案，辅助标准化、目视化作业。",
            ],
            clients: ["机床加工", "钣金制造", "焊接切割", "管道工程", "设备检修"],
        },
    },
    {
        key: "electronic",
        label: "电子电器",
        panel: {
            title: "电子电器制造",
            bullets: [
                "覆盖家用电器、电子元器件的装配定位与质量目视化检查。",
                "配合注塑、五金等自制能力，提供零部件到整机的协同配套。",
                "灵活的定制服务，满足小批量试产与大批量生产的切换需求。",
            ],
            clients: ["小家电", "电子装配", "线束加工", "模具注塑", "整机代工"],
        },
    },
    {
        key: "lighting",
        label: "照明工程",
        panel: {
            title: "照明产品与工程应用",
            bullets: [
                "依托灯具制造能力，提供工业特种照明与工程照明配套。",
                "工业定位灯等产品可应用于室内外工程定位与指示场景。",
                "支持按项目需求定制，提供从产品到应用的一体化服务。",
            ],
            clients: ["工程照明", "特种照明", "道路亮化", "厂房照明", "定制灯具"],
        },
    },
];

const activeKey = ref("line");

const activeTab = computed(() => tabs.find((t) => t.key === activeKey.value) || null);

function setActive(key) {
    activeKey.value = key;
}
</script>

<style scoped>
.sol {
    position: relative;
    padding: 92px 0;
    background: linear-gradient(180deg, #0a5fb0 0%, #0a4f97 100%);
    color: #fff;
    overflow: hidden;
}

/* 背景纹理（轻微网格+光晕） */
.sol::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
        radial-gradient(circle at 20% 70%, rgba(255, 255, 255, .10), transparent 45%),
        radial-gradient(circle at 75% 35%, rgba(255, 255, 255, .08), transparent 42%),
        repeating-linear-gradient(90deg, rgba(255, 255, 255, .06) 0 1px, transparent 1px 110px),
        repeating-linear-gradient(0deg, rgba(255, 255, 255, .05) 0 1px, transparent 1px 110px);
    opacity: .35;
    pointer-events: none;
}

.container {
    position: relative;
    z-index: 1;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
}

.sol-title {
    margin: 0;
    text-align: center;
    font-size: 34px;
    font-weight: 800;
    letter-spacing: 2px;
}

.sol-subtitle {
    margin: 16px auto 0;
    max-width: 980px;
    text-align: center;
    color: rgba(255, 255, 255, .85);
    line-height: 1.9;
    font-size: 14.5px;
}

.panel {
    margin-top: 40px;
    display: flex;
    min-height: 380px;
}

.left {
    width: 220px;
    flex: 0 0 auto;
}

.menu {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 14px;
}

.menu-btn {
    width: 180px;
    height: 48px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, .18);
    background: transparent;
    color: rgba(255, 255, 255, .85);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    transition: background-color .15s ease, color .15s ease, border-color .15s ease;
}

.menu-btn:hover {
    border-color: rgba(255, 255, 255, .28);
}

.menu-btn.active {
    background: #fff;
    color: #0a5fb0;
    border-color: #fff;
    font-weight: 700;
    box-shadow: 0 10px 24px rgba(0, 0, 0, .14);
}

.divider {
    width: 1px;
    margin: 0 32px;
    background: rgba(255, 255, 255, .18);
}

.right {
    flex: 1;
    padding-top: 4px;
}

.right-title {
    margin: 0;
    font-size: 28px;
    font-weight: 800;
}

.bullets {
    margin: 18px 0 0;
    padding-left: 18px;
    color: rgba(255, 255, 255, .88);
    line-height: 2.0;
    font-size: 14px;
}

.clients {
    margin-top: 24px;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    max-width: 760px;
}

.client {
    height: 52px;
    background: rgba(255, 255, 255, .92);
    color: #0a4f97;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 13px;
}

.actions {
    margin-top: 26px;
    display: flex;
    gap: 14px;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 42px;
    padding: 0 22px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 700;
    border: 1px solid rgba(255, 255, 255, .35);
    background: transparent;
    color: #fff;
    text-decoration: none;
    transition: background-color .15s ease, border-color .15s ease;
}

.btn-ghost:hover {
    background: rgba(255, 255, 255, .10);
    border-color: rgba(255, 255, 255, .55);
}

.btn-outline {
    border-color: rgba(255, 255, 255, .35);
}

.btn-outline:hover {
    background: rgba(255, 255, 255, .10);
    border-color: rgba(255, 255, 255, .55);
}

/* 响应式 */
@media (max-width: 980px) {
    .sol {
        padding: 64px 0;
    }

    .panel {
        flex-direction: column;
    }

    .left {
        width: 100%;
    }

    .menu {
        grid-template-columns: repeat(3, 1fr);
    }

    .menu-btn {
        width: 100%;
    }

    .divider {
        width: 100%;
        height: 1px;
        margin: 20px 0;
    }

    .clients {
        grid-template-columns: repeat(3, 1fr);
        max-width: 100%;
    }
}

@media (max-width: 620px) {
    .sol-title {
        font-size: 26px;
    }

    .menu {
        grid-template-columns: repeat(2, 1fr);
    }

    .clients {
        grid-template-columns: repeat(2, 1fr);
    }

    .right-title {
        font-size: 22px;
    }
}
</style>
